import { Avatar, Menu } from "@mui/material";
import { FC, MouseEventHandler, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  useApplication,
  useEmitterListener,
  useModalConfirm,
} from "@ryde/redux";
import {
  ConfirmResult,
  getOldSystemUrl,
  GlobalAppEventEnum,
  Log,
  profileDtoAtom,
  stringAvatar,
} from "@ryde/shared";
import { getMenuItemColor } from "@ryde/theme";

import {
  LeftBarBottomIconWrap,
  LeftBarBottomMenuUl,
  LeftBarButton,
  LeftBarHelpPageButton,
  LeftBarNotificationBell,
  LeftBarProfileContext,
  StyledBadgeAvatar,
} from "./parts";

export const LeftBarBottom: FC = () => {
  const { isMainApp } = useApplication();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const profile = useRecoilValue(profileDtoAtom);
  const location = useLocation();
  const { confirmDialogResult } = useModalConfirm();
  const userFullName = profile?.lastName
    ? `${profile.lastName} ${profile.firstName || ""}`
    : "User";
  const open = Boolean(anchorEl);
  const handleAvatarClick: MouseEventHandler<HTMLDivElement> = useEvent(e => {
    setAnchorEl(e.currentTarget);
  });

  const handleMenuClose = useCallback((): void => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  useEmitterListener({
    event: GlobalAppEventEnum.START_DRAG,
    handler: handleMenuClose,
    isEnabled: open,
  });

  const goToOldSystem: MouseEventHandler<HTMLDivElement> = useEvent(
    async (e: React.MouseEvent): Promise<void> => {
      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t(`general.GO_TO_OLD_SYSTEM_MESSAGE`),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        Log.warnAndToast(t("errors.moveToOldSystemCanceledByUser"));
        return;
      }

      const navigateUrl = getOldSystemUrl(location.pathname, location.search);
      Log.debug(
        `Converted old system url: ${navigateUrl}, from path: "${
          location.pathname
        }"${location.search ? ",search:" + location.search : ""}`,
      );
      e.preventDefault();
      Log.info(t("general.NAV_TO_OLD_SYS"));
      window.location.href = navigateUrl;
    },
  );

  return (
    <>
      <LeftBarBottomMenuUl>
        <LeftBarHelpPageButton />
        <LeftBarButton
          onMenuItemClick={goToOldSystem}
          tooltip={t(`general.GO_TO_OLD_SYSTEM`)}
        >
          <LeftBarBottomIconWrap
            className="left-bar-bottom-icon-wrap"
            $color={getMenuItemColor(false)}
          >
            <SVG_MUI.SwitchToOld4AdminSVG
              className="svg-go-to-old rtl_rotate"
              data-e2e="svg-old-system"
            />
          </LeftBarBottomIconWrap>
        </LeftBarButton>
        {isMainApp && <LeftBarNotificationBell />}
        <li className="avatar-container" data-e2e="avatar-container">
          <StyledBadgeAvatar
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
          >
            <Avatar
              onClick={handleAvatarClick}
              alt={userFullName || t("general.NO_NAME")}
              {...stringAvatar(userFullName || t("general.NO_NAME"))}
            />
          </StyledBadgeAvatar>
        </li>
      </LeftBarBottomMenuUl>
      <Menu
        id="lock-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            },
          },
        }}
      >
        <LeftBarProfileContext handleMenuClose={handleMenuClose} />
      </Menu>
    </>
  );
};
